import React, { useEffect, useState } from 'react';
import { Box, VStack, HStack, Text, Icon, Spinner, Center } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { CompanyLogoHeader } from '../../components/auth/CompanyLogoHeader';
import Button from '../../components/base/Button';
import { useTranslation } from '../../hooks/useTranslation';
import { RegistrationEmailStep } from './RegistrationEmailStep';
import { RegistrationConfirmEmailStep } from './RegistrationConfirmEmailStep';
import { useGetRegCustomerQuery } from '../../api';

type RegistrationStep = 'email' | 'confirm';

const RegistrationPage: React.FC = () => {
    const { t } = useTranslation({ setLangFromURL: true });
    const navigate = useNavigate();
    const [step, setStep] = useState<RegistrationStep>('email');
    const [submittedEmail, setSubmittedEmail] = useState('');

    const { data: regCustomer, isLoading, status } = useGetRegCustomerQuery();

    useEffect(() => {
        if (status === 'fulfilled' && regCustomer) {
            navigate('/registration/form', { replace: true });
        }
    }, [regCustomer, navigate]);

    const handleEmailSubmit = (email: string) => {
        setSubmittedEmail(email);
        setStep('confirm');
    };

    const handleChangeEmail = () => {
        setStep('email');
    };

    if (isLoading) {
        return (
            <Box minH="100dvh" bg="gray.100" display="flex" alignItems="center" justifyContent="center" p={4}>
                <Center flexDirection="column" gap={4}>
                    <Spinner size="lg" color="yellow.600" />
                    <Text fontSize="md" color="gray.700">
                        {t.registration.form.loadingRegCustomerData}
                    </Text>
                </Center>
            </Box>
        );
    }

    return (
        <Box minH="100dvh" bg="gray.100" display="flex" alignItems="center" justifyContent="center" p={4}>
            <Box w="full" maxW="md" bg="white" p={6}>
                <VStack gap={8} align="stretch">
                    {step !== 'email' && (
                        <Button
                            type="button"
                            buttonType="ghost"
                            size="sm"
                            onClick={handleChangeEmail}
                            aria-label={t.registration.confirmBack}
                            alignSelf="flex-start"
                            pl={0}
                        >
                            <HStack gap={2}>
                                <Icon as={FiArrowLeft} boxSize={5} />
                                <Text>{t.registration.confirmBack}</Text>
                            </HStack>
                        </Button>
                    )}
                    <CompanyLogoHeader />

                    {step === 'email' && <RegistrationEmailStep onSuccess={handleEmailSubmit} />}
                    {step === 'confirm' && <RegistrationConfirmEmailStep email={submittedEmail} />}
                </VStack>
            </Box>
        </Box>
    );
};

export default RegistrationPage;
